import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import {
  GET_MY_ADS_LIST,
  getCurrentUserAdStatus,
  MyAdsListResponse,
  MyAdsListVariables,
} from '@/features/Profile/sections/MyAds/api';
import { AdStatus } from '@/types';

export function useMyAdsList(selectedAdStatus: AdStatus) {
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine
  );
  const queryStatus = getCurrentUserAdStatus(selectedAdStatus);
  const { data, loading, fetchMore } = useQuery<
    MyAdsListResponse,
    MyAdsListVariables
  >(GET_MY_ADS_LIST, {
    variables: {
      status: queryStatus,
      first: AdsChunkSize.Middle,
    },
    fetchPolicy: 'network-only',
  });

  const ads = data?.currentUserAds?.nodes ?? [];
  const { endCursor, hasNextPage } = data?.currentUserAds?.pageInfo ?? {};
  const canLoadMore = !!hasNextPage && isOnline;

  useEffect(() => {
    function updateOnlineStatus() {
      setIsOnline(navigator.onLine);
    }

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  function handleLoadMore() {
    if (loading || isLoadingMore || !canLoadMore) return;

    setLoadingMore(true);
    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!prevResult.currentUserAds || !fetchMoreResult?.currentUserAds) {
          return prevResult;
        }

        return {
          ...prevResult,
          currentUserAds: {
            ...prevResult.currentUserAds,
            nodes: [
              ...(prevResult.currentUserAds.nodes ?? []),
              ...(fetchMoreResult.currentUserAds.nodes ?? []),
            ],
            pageInfo: fetchMoreResult.currentUserAds.pageInfo,
          },
        };
      },
    }).finally(() => setLoadingMore(false));
  }

  return {
    ads,
    canLoadMore,
    handleLoadMore,
    isLoadingMore,
    loading,
  };
}
